import React, { useState, useEffect } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import { useLanguage } from '../context/LanguageContext';
import { useOrbitAuth } from '../context/OrbitAuthContext';
import { User, Eye, EyeOff, Save, MapPin, Phone, Mail, Globe, MessageCircle } from 'lucide-react';
import { Planet, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MyProfilePage = () => {
  const { language } = useLanguage();
  const { user, token, setUser } = useOrbitAuth();
  const t = (fr, en) => language === 'fr' ? fr : en;

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', city: '', neighborhood: '',
    province_code: '', postal_code: '', country: '',
    bio_fr: '', bio_en: '',
    website: '', facebook: '', instagram: '', linkedin: '',
    messenger_link: '',
    is_visible_in_community: false,
    show_email: true, show_phone: true, show_city: true,
    show_socials: true, show_rank: true, show_messenger: true,
  });

  useEffect(() => {
    if (user) {
      const privacy = user.privacy || {};
      const socials = user.socials || {};
      setForm({
        name: user.name || '',
        phone: user.phone || '',
        city: user.city || '',
        neighborhood: user.neighborhood || '',
        province_code: user.province_code || '',
        postal_code: user.postal_code || '',
        country: user.country || '',
        bio_fr: user.bio_fr || '',
        bio_en: user.bio_en || '',
        website: socials.website || '',
        facebook: socials.facebook || '',
        instagram: socials.instagram || '',
        linkedin: socials.linkedin || '',
        messenger_link: socials.messenger_link || '',
        is_visible_in_community: user.is_visible_in_community || false,
        show_email: privacy.show_email !== false,
        show_phone: privacy.show_phone !== false,
        show_city: privacy.show_city !== false,
        show_socials: privacy.show_socials !== false,
        show_rank: privacy.show_rank !== false,
        show_messenger: privacy.show_messenger !== false,
      });
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/orbit/profile`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setUser(updated);
      toast.success(t('Profil mis a jour !', 'Profile updated!'));
    } catch { toast.error(t('Erreur de sauvegarde', 'Save error')); }
    finally { setSaving(false); }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const inputClass = "w-full px-4 py-2.5 rounded-xl border border-[hsl(40,20%,88%)] bg-white text-[var(--ls-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ls-terracotta)]/20 focus:border-[var(--ls-terracotta)] transition-all";

  const ToggleSwitch = ({ checked, onChange, label, description }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-medium text-[var(--ls-text)]">{label}</p>
        {description && <p className="text-xs text-[var(--ls-text-muted)] mt-0.5">{description}</p>}
      </div>
      <button onClick={() => onChange(!checked)} type="button"
        className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-[var(--ls-terracotta)]' : 'bg-gray-200'}`}
        data-testid={`toggle-${label.toLowerCase().replace(/\s/g, '-')}`}>
        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5.5 left-0.5' : 'left-0.5'}`}
          style={{ transform: checked ? 'translateX(22px)' : 'translateX(0)' }} />
      </button>
    </div>
  );

  return (
    <OrbitLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="my-profile-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">
              {t('Mon Profil', 'My Profile')}
            </h1>
            <p className="text-gray-300 mt-1">{t('Gerez vos informations et votre visibilite', 'Manage your info and visibility')}</p>
          </div>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-[var(--ls-terracotta)] text-white rounded-full font-semibold text-sm hover:bg-[var(--ls-terracotta-dark)] transition-colors disabled:opacity-50 shadow-lg shadow-[var(--ls-terracotta)]/20"
            data-testid="save-profile-btn">
            {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
            {t('Sauvegarder', 'Save')}
          </button>
        </div>

        {/* Visibility Toggle */}
        <div className={`rounded-2xl p-5 border transition-all ${
          form.is_visible_in_community
            ? 'bg-[var(--ls-terracotta)]/5 border-[var(--ls-terracotta)]/20'
            : 'bg-white border-[hsl(40,20%,88%)]'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {form.is_visible_in_community
                ? <Eye className="w-5 h-5 text-[var(--ls-terracotta)]" />
                : <EyeOff className="w-5 h-5 text-[var(--ls-text-muted)]" />}
              <div>
                <h3 className="font-heading font-bold text-[var(--ls-text)]">
                  {t('Visible dans la communaute', 'Visible in the community')}
                </h3>
                <p className="text-xs text-[var(--ls-text-muted)] mt-0.5">
                  {form.is_visible_in_community
                    ? t('Les autres membres peuvent vous trouver dans l\'annuaire', 'Other members can find you in the directory')
                    : t('Vous n\'apparaissez pas dans l\'annuaire communautaire', 'You do not appear in the community directory')}
                </p>
              </div>
            </div>
            <button onClick={() => update('is_visible_in_community', !form.is_visible_in_community)} type="button"
              className={`relative w-12 h-7 rounded-full transition-colors ${form.is_visible_in_community ? 'bg-[var(--ls-terracotta)]' : 'bg-gray-200'}`}
              data-testid="toggle-community-visibility">
              <div className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform"
                style={{ left: '2px', transform: form.is_visible_in_community ? 'translateX(22px)' : 'translateX(0)' }} />
            </button>
          </div>
        </div>

        {/* Personal Info */}
        <div className="bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-5 space-y-4">
          <h3 className="font-heading font-bold text-[var(--ls-text)] flex items-center gap-2">
            <User className="w-4 h-4 text-[var(--ls-terracotta)]" />{t('Informations personnelles', 'Personal Information')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">{t('Nom complet', 'Full name')}</label>
              <input type="text" value={form.name} onChange={e => update('name', e.target.value)}
                className={inputClass} data-testid="profile-name" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">{t('Telephone', 'Phone')}</label>
              <input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)}
                className={inputClass} placeholder="514-555-1234" data-testid="profile-phone" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">{t('Ville', 'City')}</label>
              <input type="text" value={form.city} onChange={e => update('city', e.target.value)}
                className={inputClass} data-testid="profile-city" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">{t('Code postal', 'Postal code')}</label>
              <input type="text" value={form.postal_code} onChange={e => update('postal_code', e.target.value)}
                className={inputClass} data-testid="profile-postal" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">{t('Bio (Francais)', 'Bio (French)')}</label>
              <textarea value={form.bio_fr} onChange={e => update('bio_fr', e.target.value)} rows={2}
                className={`${inputClass} resize-none`} placeholder={t('Parlez de vous...', 'Tell us about yourself...')}
                data-testid="profile-bio-fr" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">{t('Bio (Anglais)', 'Bio (English)')}</label>
              <textarea value={form.bio_en} onChange={e => update('bio_en', e.target.value)} rows={2}
                className={`${inputClass} resize-none`} placeholder={t('Tell us about yourself...', 'Tell us about yourself...')}
                data-testid="profile-bio-en" />
            </div>
          </div>
        </div>

        {/* Contact Options */}
        <div className="bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-5 space-y-4">
          <h3 className="font-heading font-bold text-[var(--ls-text)] flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-[var(--ls-forest)]" />{t('Options de contact', 'Contact Options')}
          </h3>
          <p className="text-xs text-[var(--ls-text-muted)]">
            {t('Choisissez comment les autres membres peuvent vous contacter (tout est externe a LocalSite.Club)',
              'Choose how other members can contact you (everything is external to LocalSite.Club)')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1 flex items-center gap-1">
                <Globe className="w-3 h-3" />{t('Site web', 'Website')}
              </label>
              <input type="url" value={form.website} onChange={e => update('website', e.target.value)}
                className={inputClass} placeholder="https://" data-testid="profile-website" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1 flex items-center gap-1">
                <MessageCircle className="w-3 h-3" />Facebook Messenger
              </label>
              <input type="url" value={form.messenger_link} onChange={e => update('messenger_link', e.target.value)}
                className={inputClass} placeholder="https://m.me/votre-nom" data-testid="profile-messenger" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">Facebook</label>
              <input type="url" value={form.facebook} onChange={e => update('facebook', e.target.value)}
                className={inputClass} placeholder="https://facebook.com/..." data-testid="profile-facebook" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">Instagram</label>
              <input type="url" value={form.instagram} onChange={e => update('instagram', e.target.value)}
                className={inputClass} placeholder="https://instagram.com/..." data-testid="profile-instagram" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">LinkedIn</label>
              <input type="url" value={form.linkedin} onChange={e => update('linkedin', e.target.value)}
                className={inputClass} placeholder="https://linkedin.com/in/..." data-testid="profile-linkedin" />
            </div>
          </div>
        </div>

        {/* Privacy Settings */}
        <div className="bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-5 space-y-1">
          <h3 className="font-heading font-bold text-[var(--ls-text)] flex items-center gap-2 mb-2">
            <EyeOff className="w-4 h-4 text-[var(--ls-gold)]" />{t('Confidentialite', 'Privacy')}
          </h3>
          <p className="text-xs text-[var(--ls-text-muted)] mb-3">
            {t('Controlez quelles informations sont visibles par les autres membres',
              'Control which information is visible to other members')}
          </p>
          <div className="divide-y divide-[hsl(40,20%,88%)]">
            <ToggleSwitch checked={form.show_email} onChange={v => update('show_email', v)}
              label={t('Afficher le courriel', 'Show email')} />
            <ToggleSwitch checked={form.show_phone} onChange={v => update('show_phone', v)}
              label={t('Afficher le telephone', 'Show phone')} />
            <ToggleSwitch checked={form.show_city} onChange={v => update('show_city', v)}
              label={t('Afficher la ville', 'Show city')} />
            <ToggleSwitch checked={form.show_rank} onChange={v => update('show_rank', v)}
              label={t('Afficher le rang', 'Show rank')} />
            <ToggleSwitch checked={form.show_socials} onChange={v => update('show_socials', v)}
              label={t('Afficher les reseaux sociaux', 'Show social links')} />
            <ToggleSwitch checked={form.show_messenger} onChange={v => update('show_messenger', v)}
              label={t('Afficher Messenger', 'Show Messenger')} />
          </div>
        </div>

        {/* Save Button Bottom */}
        <div className="flex justify-end">
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--ls-terracotta)] text-white rounded-full font-semibold hover:bg-[var(--ls-terracotta-dark)] transition-colors disabled:opacity-50 shadow-lg shadow-[var(--ls-terracotta)]/20"
            data-testid="save-profile-btn-bottom">
            {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-4 h-4" weight="bold" />}
            {t('Sauvegarder le profil', 'Save profile')}
          </button>
        </div>
      </div>
    </OrbitLayout>
  );
};

export default MyProfilePage;
